import * as React from 'react';

//other deps
import _ from 'lodash';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { Localized } from '@fluent/react';
import Spinner from 'react-loader-spinner';
import { FluentBundle } from '@fluent/bundle';
import { Dispatch, bindActionCreators } from 'redux';
import { push as pushAction } from 'connected-react-router';

//api
import * as api from 'app/api';
import { RecognizeApiModel, UserApiModel } from 'app/api/types';

//components
import * as Icons from 'app/components/Icons';
import Checkbox from 'app/sharedComponents/Checkbox';
import FileUpload from 'app/sharedComponents/FileUpload';

import Builder from 'app/containers/Builder';

//redux
import { RootState } from 'app/reducers';
import { UserActions } from 'app/actions';

//schemes
import * as schemes from '../../../schemes';

//types
import {
	UserProfile,
	RoleEnum,
	BankAccount,
	DocumentTypeEnum,
	Passport,
	DriverLicence,
	DocumentAttributesMap,
	isPassportMainPage,
	isPassportRegPage,
	isDriverMainPage,
	isDriverBackPage,
} from 'app/models';

import { SchemeProps } from 'app/containers/Builder/props';

type DocumentType = (typeof documentTypes)[number];

//helpers
import { mapProfileCertificates } from './form.helpers';

//utils
import { changeDateFormat } from 'app/utils';

//constants
import {
	certificateLocalizedNameMap,
	certificateTypes,
	documentLocalizedLabelMap,
	documentTypeMap,
	documentTypes,
} from './form.constants';

//----------------------------------------------------------
// DocsProfileForm
//----------------------------------------------------------
class DocsProfileForm extends React.Component<DocsProfileForm.Props, DocsProfileForm.State> {
	public builderDriverLicence: Builder | null = null;
	public builderBankAccount: Builder | null = null;
	public builderPassport: Builder | null = null;

	public state: DocsProfileForm.State = {
		interestedInDigitalSignature: false,

		passportScheme: _.cloneDeep(schemes.passport),
		driverLicenceScheme: _.cloneDeep(schemes.driverLicence),
		bankScheme: _.cloneDeep(schemes.company),

		passportForm: {},
		driverLicenceForm: {},
		bankAccountForm: {},

		loading: false,
		valid: false,
	};

	constructor(props: DocsProfileForm.Props) {
		super(props);
	}

	private getLocalizedMessage = (id: string): string => {
		const { locales } = this.props;
		if (!locales) return '';

		const [locale] = locales;
		return locale.getMessage(id)?.value?.toString() || '';
	};

	public componentDidMount() {
		this.initializeBuilderWithProps();
	}

	private initializeBuilderWithProps = () => {
		const { builderDriverLicence, builderPassport, builderBankAccount } = this;
		const { profile } = this.props;

		if (
			// Check initialized builders
			builderDriverLicence &&
			builderBankAccount &&
			builderPassport &&
			// Check role
			profile &&
			profile.driverDocs &&
			profile.role === RoleEnum.Driver
		) {
			const {
				driverDocs: { passport, driverLicence, bankAccount },
			} = profile;

			if (passport) {
				this.updateFormFields(passport, builderPassport);
			}

			if (driverLicence) {
				this.updateFormFields(driverLicence, builderDriverLicence);
			}

			if (bankAccount) {
				this.updateFormFields(bankAccount, builderBankAccount);
			}
		}
	};

	private updateFormFields = (patch: Partial<Passport | DriverLicence | BankAccount>, builder: Builder) => {
		Object.entries(patch).forEach(([key, value]) => {
			const ref = builder.formRefs.get(key);

			if (ref && value) {
				ref.updateValue(value);
			}
		});
	};

	private mapRecognizedDocumentFields = <T extends keyof DocumentAttributesMap>({
		documentType,
		attributes: data,
	}: RecognizeApiModel.ProcessResponseData<T>) => {
		const { builderPassport, builderDriverLicence } = this;
		if (!builderPassport || !builderDriverLicence) return;

		let patch: Partial<Passport | DriverLicence> = {};
		let activeBuilder: Builder | null = null;

		if (isPassportMainPage(documentType, data)) {
			activeBuilder = builderPassport;

			patch = {
				number: data.number?.value || undefined,
				issuer: data.issuer?.value || undefined,
				issuerCode: data.issuerCode?.value || undefined,
				birthplace: data.birthplace?.value || undefined,
				issuedAt: changeDateFormat('DD.MM.YYYY', 'YYYY-MM-DD', data.issuedAt?.value) || undefined,
				birthday: changeDateFormat('DD.MM.YYYY', 'YYYY-MM-DD', data.birthday?.value) || undefined,
			};
		} else if (isPassportRegPage(documentType, data)) {
			activeBuilder = builderPassport;

			patch = {
				registrationAddress: data.registrationAddress?.value || undefined,
			};
		} else if (isDriverMainPage(documentType, data)) {
			activeBuilder = builderDriverLicence;

			patch = {
				number: data.number?.value || undefined,
				issuedAt: changeDateFormat('DD.MM.YYYY', 'YYYY-MM-DD', data.issuedAt?.value) || undefined,
				expiredAt: changeDateFormat('DD.MM.YYYY', 'YYYY-MM-DD', data.expiredAt?.value) || undefined,
			};
		} else if (isDriverBackPage(documentType, data)) {
			activeBuilder = builderDriverLicence;

			patch = {
				number: data.number?.value || undefined,
			};
		}

		if (activeBuilder) {
			this.updateFormFields(patch, activeBuilder);
			activeBuilder.isValid();
		}
	};

	private documentRecognizeProcess = async (docType: DocumentType, name: string, value: any) => {
		const documentType = documentTypeMap[`${docType}-${name}`];
		if (!documentType || !value) return;

		let failedMessage: string | null = null;
		const documentLocalizedName = documentLocalizedLabelMap[`${docType}-${name}`];

		this.setState({ loading: true });

		try {
			const data = await api.recognizeProcess({
				documentType: documentType,
				documentFile: value,
			});

			if (data.documentType === DocumentTypeEnum.Invalid) {
				failedMessage = documentLocalizedName;
			} else if (data.documentType !== documentType) {
				failedMessage = documentLocalizedName;
			} else {
				this.mapRecognizedDocumentFields(data);
			}
		} catch (error) {
			failedMessage = documentLocalizedName;
		}

		this.setState({ loading: false });

		if (failedMessage) {
			const errorTitle = this.getLocalizedMessage('profile-alert-docs-recognize-failed');
			const errorDescription = this.getLocalizedMessage(failedMessage);
			const errorMessage = `${errorTitle} ${errorDescription}`;

			toast.error(errorMessage, {
				autoClose: 5000,
				position: toast.POSITION.BOTTOM_CENTER,
			});
		} else {
			toast.success(this.getLocalizedMessage('profile-alert-updated'), {
				position: toast.POSITION.BOTTOM_CENTER,
			});
		}
	};

	private onUpdate = (docType: DocumentType) => {
		return (name: string, value: any) => {
			const formKey = `${docType}Form`;
			const form = (this.state as any)[formKey];

			this.setState({
				[formKey]: {
					...form,
					[name]: value,
				},
			} as DocsProfileForm.State);

			//[id-engine] recognize document
			this.documentRecognizeProcess(docType, name, value);
		};
	};

	private isValid = (docsType: DocumentType) => {
		const { passportForm, driverLicenceForm, bankAccountForm } = this.state;
		const { profile } = this.props;

		if (profile && profile.driverDocs && profile.role === RoleEnum.Driver) {
			const { passport, driverLicence } = profile.driverDocs;

			switch (docsType) {
				case 'passport':
					return (
						passportForm.issuedAt &&
						passportForm.issuedAt.length > 0 &&
						passportForm.number &&
						passportForm.number.length > 0 &&
						passportForm.issuer &&
						passportForm.issuer.length > 0 &&
						(!!passportForm.registrationPage || !!passport.registrationPage) &&
						(!!passportForm.mainPage || !!passport.mainPage)
					);

				case 'driverLicence':
					return (
						driverLicenceForm.expiredAt &&
						driverLicenceForm.expiredAt.length > 0 &&
						driverLicenceForm.issuedAt &&
						driverLicenceForm.issuedAt.length > 0 &&
						driverLicenceForm.number &&
						driverLicenceForm.number.length > 0 &&
						(!!driverLicenceForm.mainPage || !!driverLicence.mainPage) &&
						(!!driverLicenceForm.backPage || !!driverLicence.backPage)
					);

				case 'bankAccount':
					return (
						bankAccountForm.account &&
						bankAccountForm.account.length === 20 &&
						bankAccountForm.bic &&
						bankAccountForm.bic.length === 9 &&
						bankAccountForm.bic.substr(0, 2) === '04'
					);

				default:
					return false;
			}
		} else {
			return false;
		}
	};

	private onSave = async () => {
		const { sessionToken, setProfile, updateDocs, profile } = this.props;
		const { interestedInDigitalSignature, driverLicenceForm, bankAccountForm, passportForm, loading } = this.state;

		if (sessionToken && setProfile && updateDocs && !loading && profile) {
			const updated: UserActions.Payload.Docs = {};

			const failed: [string, Error][] = [];
			const success: string[] = [];

			this.setState({ loading: true });

			if (interestedInDigitalSignature) {
				// @TODO: fix that shit api call in back issue
				const paramsProfile = {
					interestedInDigitalSignature,

					companyName: profile.companyName,
					middleName: profile.middleName,
					firstName: profile.firstName,
					legalType: profile.legalType,
					lastName: profile.lastName,
					email: profile.email,
					inn: profile.inn,
				};

				await api
					.userUpdateProfile(paramsProfile)
					.then((profile) => {
						setProfile(profile);
					})
					.catch((error) => {
						console.error(error);
					});
			}

			await Promise.all([
				api
					.userUpdatePassport({ ...passportForm })
					.then((passport) => {
						success.push('profile-header-passport');
						updated.passport = passport;
					})
					.catch((error) => {
						failed.push(['profile-header-passport', error]);
					}),

				api
					.userUpdateBankAccount({ ...bankAccountForm } as BankAccount)
					.then((bankAccount) => {
						success.push('profile-org-header');
						updated.bankAccount = bankAccount;
					})
					.catch((error) => {
						failed.push(['profile-org-header', error]);
					}),

				api
					.userUpdateDriverLicence({ ...driverLicenceForm })
					.then((driverLicence) => {
						success.push('profile-header-driver-licence');
						updated.driverLicence = driverLicence;
					})
					.catch((error) => {
						failed.push(['profile-header-driver-licence', error]);
					}),
			]);

			if (updateDocs) {
				updateDocs(updated);
			}

			this.setState({ loading: false });

			if (failed.length > 0) {
				failed.forEach(([message]) => {
					console.error(message);
				});

				const failedMessages = failed.map(([message]) => this.getLocalizedMessage(message));
				const errorMessage = `${this.getLocalizedMessage('profile-alert-docs-updated-failed')} ${failedMessages.join(
					', ',
				)}`;

				toast.error(errorMessage, {
					autoClose: 5000,
					position: toast.POSITION.BOTTOM_CENTER,
				});
			} else {
				toast.success(this.getLocalizedMessage('profile-alert-updated'), {
					position: toast.POSITION.BOTTOM_CENTER,
				});

				const passportValid = this.isValid('passport');
				const driverLicenceValid = this.isValid('driverLicence');
				const bankAccountValid = this.isValid('bankAccount');

				if (passportValid && driverLicenceValid && bankAccountValid) {
					this.props.toggleEditable(false);
					// Show modal if all data approved
					this.props.openSendedModal();
				}
			}
		}
	};

	private getDigitalSignature = () => {
		const { interestedInDigitalSignature } = this.state;
		const { profile } = this.props;

		const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const checked = e.target.checked;

			this.setState({
				interestedInDigitalSignature: checked,
			});
		};

		return profile && profile.interestedInDigitalSignature ? (
			<div className='alert panel'>
				<div className='icon'>
					<Icons.Check color='#AEB4C8' />
				</div>
				<div className='title'>
					<Localized id='profile-digital-signature-requested' />
				</div>
			</div>
		) : (
			<div className='need-digital-signature'>
				<Checkbox checked={interestedInDigitalSignature} onChange={onChange}>
					<Localized id='profile-digital-signature-needed' />
				</Checkbox>

				<a className='action' target='_blank' href='/panel/faq/verify'>
					<Localized id='faq-button-verify-text' />
				</a>
			</div>
		);
	};

	private outputModeratorMessages(builder?: Builder | null, key?: 'passport' | 'driverLicence') {
		const { profile } = this.props;
		if (!profile || !builder || !key) return;

		const driverDocs = profile.driverDocs;
		if (!driverDocs) return;

		const doc = driverDocs[key];
		if (!doc) return;

		builder.outputOperatorCommentary(doc.verification);
	}

	uploadCertificate = (kind: string) => async (document: any) => {
		const { addUserCertificate, sessionToken } = this.props;

		try {
			if (addUserCertificate && sessionToken) {
				const certificate = await api.userCertificate({ document }, 'post', kind);
				addUserCertificate(certificate);
			}
		} catch (error) {
			console.error(error);
		}

		return Promise.resolve();
	};

	deleteCertificate = async (document: any) => {
		const { deleteUserCertificate } = this.props;

		try {
			if (deleteUserCertificate) {
				deleteUserCertificate(document.id);
			}
		} catch (error) {
			console.log(error);
		}
	};

	public render() {
		const { passportScheme, driverLicenceScheme, bankScheme, loading } = this.state;
		const { profile } = this.props;

		const loadingClass = loading ? 'loading' : '';

		const passportBuilderProps: Builder['props'] = {
			getRef: (node: Builder | null) => {
				this.builderPassport = node;
				this.outputModeratorMessages(node, 'passport');
			},
			onUpdate: this.onUpdate('passport'),
			scheme: passportScheme.map((el: SchemeProps.Union) => ({
				...el,
				disabled: profile?.driverDocs?.passport?.verification?.status === 'verified',
			})),
		};

		const driverLicenceBuilderProps: Builder['props'] = {
			getRef: (node: Builder | null) => {
				this.builderDriverLicence = node;
				this.outputModeratorMessages(node, 'driverLicence');
			},
			onUpdate: this.onUpdate('driverLicence'),
			scheme: driverLicenceScheme.map((el: SchemeProps.Union) => ({
				...el,
				disabled: profile?.driverDocs?.driverLicence?.verification?.status === 'verified',
			})),
		};

		const bankAccountBuilderProps: Builder['props'] = {
			getRef: (node: Builder | null) => {
				this.builderBankAccount = node;
			},
			onUpdate: this.onUpdate('bankAccount'),
			scheme: bankScheme.map((el: SchemeProps.Union) => ({
				...el,
				disabled: profile?.verification?.status === 'verified',
			})),
		};

		const certificates = mapProfileCertificates(profile?.certificates);

		return (
			<div className='profile docs form'>
				<div className='profile__verify-icon'>
					<Icons.VerifyUser />
				</div>

				<div className='common'>
					<div className='header'>
						<div className='title'>
							<Localized id='profile-docs-header' />
						</div>

						<div className='subtitle'>
							<Localized id='profile-docs-description' />
						</div>

						<a className='action' target='_blank' href='/panel/faq/verify'>
							<Localized id='faq-button-verify-text' />
						</a>
					</div>

					<Builder {...passportBuilderProps} />
					<Builder {...driverLicenceBuilderProps} />
					<Builder {...bankAccountBuilderProps} />

					{certificateTypes.map((key) => (
						<FileUpload
							title={<Localized id={certificateLocalizedNameMap[key]} />}
							key={key}
							onDelete={this.deleteCertificate}
							uploaded={certificates[key]}
							onUpload={this.uploadCertificate(key)}
						/>
					))}

					{this.getDigitalSignature()}

					<div className='actions'>
						<div className={`action ${loadingClass}`} onClick={this.onSave}>
							{loading ? (
								<>
									<Spinner type='RevolvingDot' color='#1FB0E2' height={22} width={22} />
									<Localized id='profile-action-save-loading' />
								</>
							) : (
								<Localized id='profile-action-save' />
							)}
						</div>
					</div>
				</div>
			</div>
		);
	}
}

const mapStateToProps = ({ user, driver, common }: RootState) => ({
	sessionToken: user.sessionToken,
	locales: common.bundlesLocales,
	profile: user.profile,
	car: driver.carOpened,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators(
		{
			updateDocs: UserActions.updateDocs,
			setProfile: UserActions.setProfile,
			push: pushAction,
			addUserCertificate: UserActions.addUserCertificate,
			deleteUserCertificate: UserActions.deleteUserCertificate,
		},
		dispatch,
	);

export default connect<DocsProfileForm.StateProps, DocsProfileForm.DispatchProps, DocsProfileForm.ExternalProps>(
	mapStateToProps,
	mapDispatchToProps,
)(DocsProfileForm);

namespace DocsProfileForm {
	export type Props = StateProps & DispatchProps & ExternalProps;

	// Props from redux mapState
	export interface StateProps {
		locales?: FluentBundle[];
		profile?: UserProfile;
		sessionToken?: string;
	}

	// Dispatch properties function from redux
	export interface DispatchProps {
		setProfile?: (profile: UserProfile | UserApiModel.UserProfile) => void;
		updateDocs?: (docs: UserActions.Payload.Docs) => void;
		push?: (path: string, state?: any) => void;
		addUserCertificate?: (file: any) => void;
		deleteUserCertificate?: (id: string) => void;
	}

	// Props from parent element e.g <Cmp custom={true} />
	export interface ExternalProps {
		toggleEditable: (editable: boolean) => void;
		openSendedModal: () => void;
	}

	// Main component state
	export interface State {
		interestedInDigitalSignature: boolean;

		driverLicenceForm: { [name: string]: any };
		bankAccountForm: { [name: string]: any };
		passportForm: { [name: string]: any };

		driverLicenceScheme: SchemeProps.Union[];
		passportScheme: SchemeProps.Union[];
		bankScheme: SchemeProps.Union[];

		loading: boolean;
		valid: boolean;
	}
}
